import { useEVMContext } from '@vertex-protocol/react-client';
import { truncateAddress, truncateMiddle } from '@vertex-protocol/web-common';
import { useClustersName } from 'client/hooks/query/useClustersName';

interface AddressDisplayName {
  type: 'address' | 'clusters' | 'ens';
  displayName: string;
  /**
   * Truncated display name for UI purposes.
   */
  truncatedDisplayName: string;
}

/**
 * Retrieve a displayable string for an address. Priority:
 * - Clusters name (if available)
 * - Truncated address
 */
export function useConnectedAddressDisplayName(): AddressDisplayName {
  const {
    connectionStatus: { address },
  } = useEVMContext();
  const { data: clustersName } = useClustersName(address);

  const defaultAddressDisplayName: AddressDisplayName = {
    type: 'address',
    displayName: address ?? '',
    truncatedDisplayName: truncateAddress(address ?? ''),
  };
  // Avoid flicker in UI by returning undefined until Clusters is fetched
  // undefined === not fetched, null === fetched but no name
  if (clustersName === undefined) {
    return defaultAddressDisplayName;
  }

  if (clustersName) {
    return {
      type: 'clusters',
      displayName: clustersName,
      truncatedDisplayName: truncateMiddle(clustersName ?? '', 7),
    };
  }

  return defaultAddressDisplayName;
}
